import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CountriesMenu from './CountriesMenu';
import { COUNTRIES, formatUSD } from '../data/countries';

export default function TopBar({ variant = 'home', breadcrumb }){
  const loc = useLocation();
  const compact = variant === 'page';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileQuery, setMobileQuery] = useState('');

  useEffect(() => { setMobileOpen(false); setMobileQuery(''); }, [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const mobileResults = useMemo(() => {
    const q = mobileQuery.trim().toLowerCase();
    if (!q) return null;
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.visaName.toLowerCase().includes(q) ||
      (c.capital || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [mobileQuery]);

  return (
    <>
      <header className={`topbar ${compact ? 'compact' : ''}`}>
        <Link to="/" className="brand">
          <img className="brand-logo" src="/nomad-compass-logo.png" alt="Nomad Compass" width="800" height="250" />
        </Link>
        <nav className="nav" aria-label="Primary">
          <CountriesMenu trigger="Countries" />
          <Link to="/quiz" className={loc.pathname === '/quiz' ? 'current' : ''}>Visa Quiz</Link>
          <Link to="/visit-visas" className={loc.pathname === '/visit-visas' ? 'current' : ''} data-testid="nav-visit-visas">Visit Visas</Link>
          <Link to="/compare" className={loc.pathname === '/compare' ? 'current' : ''} data-testid="nav-compare">Compare</Link>
          <Link to="/nomad-index" className={loc.pathname === '/nomad-index' ? 'current' : ''} data-testid="nav-index">Nomad Index</Link>
          <Link to="/blog" className={loc.pathname.startsWith('/blog') ? 'current' : ''}>Journal</Link>
          {!compact && <Link to="/#why">Why us</Link>}
          {!compact && <Link to="/#faq">FAQ</Link>}
        </nav>
        <div className="topbar-right">
          <button
            className="nav-burger"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
            data-testid="nav-burger"
          >
            <span></span><span></span><span></span>
          </button>
          <Link to={loc.pathname.startsWith('/countries/') ? '#enquiry' : '/#enquiry'} className={`cta-top ${compact ? 'arrow' : ''}`}>
            <span className="cta-full">{compact ? 'Apply with our help' : 'Apply Now'}</span>
            <span className="cta-short">Apply</span>
          </Link>
        </div>
      </header>

      {mobileOpen && (
        <div className="mobile-menu" role="dialog" aria-label="Main menu">
          <div className="mobile-menu-head">
            <Link to="/" className="brand" onClick={() => setMobileOpen(false)}>
              <img className="brand-logo" src="/nomad-compass-logo.png" alt="Nomad Compass" width="800" height="250" />
            </Link>
            <button className="mobile-menu-close" aria-label="Close menu" onClick={() => setMobileOpen(false)}>×</button>
          </div>
          <nav className="mobile-nav">
            <div className="mobile-country-search">
              <label>Jump to a country</label>
              <div className="mobile-country-search-box">
                <span className="cmenu-search-icon" aria-hidden="true">⌕</span>
                <input
                  type="text"
                  value={mobileQuery}
                  onChange={e => setMobileQuery(e.target.value)}
                  placeholder="Search Portugal, Spain, Dubai…"
                  data-testid="mobile-country-search"
                />
                {mobileQuery && <button className="cmenu-clear" onClick={() => setMobileQuery('')} aria-label="Clear">×</button>}
              </div>
              {mobileResults && (
                <div className="mobile-country-results">
                  {mobileResults.length === 0 ? (
                    <div className="mobile-country-empty">No countries match "{mobileQuery}"</div>
                  ) : mobileResults.map(c => (
                    <Link
                      key={c.slug}
                      to={`/countries/${c.slug}`}
                      className="mobile-country-result"
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-cmenu-${c.slug}`}
                    >
                      <span className="cmenu-flag">{c.flag}</span>
                      <span className="cmenu-name">
                        <strong>{c.name}</strong>
                        <small>{formatUSD(c.minIncome)}/mo · {c.validity}</small>
                      </span>
                      <span className="cmenu-arrow" style={{ opacity: 1 }}>→</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/countries" onClick={() => setMobileOpen(false)}>
              <span>All countries</span>
              <small>40+ programmes, grouped by region</small>
            </Link>
            <Link to="/quiz" onClick={() => setMobileOpen(false)}>
              <span>Visa Quiz</span>
              <small>60-second matcher · top 3 results</small>
            </Link>
            <Link to="/visit-visas" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-visit-visas">
              <span>Visit Visas</span>
              <small>Upload docs · get a readiness score before you apply</small>
            </Link>
            <Link to="/compare" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-compare">
              <span>Compare visas</span>
              <small>Side-by-side: 2-3 programmes at a glance</small>
            </Link>
            <Link to="/nomad-index" onClick={() => setMobileOpen(false)}>
              <span>Nomad Index</span>
              <small>Q1 2026 · 41 visas ranked across 8 dimensions</small>
            </Link>
            <Link to="/blog" onClick={() => setMobileOpen(false)}>
              <span>Journal</span>
              <small>Essays, playbooks &amp; visa analysis</small>
            </Link>
            <Link to="/#why" onClick={() => setMobileOpen(false)}>
              <span>Why Nomad Compass</span>
              <small>Our approach &amp; who we are</small>
            </Link>
            <Link to="/#faq" onClick={() => setMobileOpen(false)}>
              <span>FAQ</span>
              <small>Common questions answered</small>
            </Link>
            <Link to="/#enquiry" onClick={() => setMobileOpen(false)} className="mobile-nav-apply">
              Apply with our help →
            </Link>
          </nav>
          <div className="mobile-menu-foot">
            Prefer browsing? <Link to="/countries" onClick={() => setMobileOpen(false)}>Open the full country directory →</Link>
          </div>
        </div>
      )}
    </>
  );
}
